/**
 * ═══════════════════════════════════════════════════════════════════════
 * ForgotPasswordPage — Request password reset + enter new password
 * ═══════════════════════════════════════════════════════════════════════
 *
 * Flow:
 *   1. Pick userType (INDIVIDUAL / ORGANISATION)
 *   2. Pick loginMethod (PHONE / EMAIL)
 *   3. Enter identifier
 *   4. Call authApi.forgotPassword() → OTP sent
 *   5. Enter OTP + new password
 *   6. Call authApi.resetPassword() → success
 *   7. Navigate to /login
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { forgotPassword, resetPassword } from '../services/auth/authApi';
import { getAuthErrorMessage } from '../services/auth/authErrors';
import { normalizeIndianMobile, validationMessage } from '../utils/phone';
import { TIMING } from '../constants';
import { ROUTES } from '../constants/routes';

import AuthCard from '../components/auth/AuthCard';
import UserTypeSelector from '../components/auth/UserTypeSelector';
import LoginMethodSelector from '../components/auth/LoginMethodSelector';
import PhoneInput from '../components/auth/PhoneInput';
import EmailInput from '../components/auth/EmailInput';
import OtpInput from '../components/auth/OtpInput';
import PasswordInput from '../components/auth/PasswordInput';
import PrimaryButton from '../components/auth/PrimaryButton';
import ResendOtp from '../components/auth/ResendOtp';

// ── Helpers ──────────────────────────────────────────────────────────

function validateEmail(email) {
  if (!email) return 'Enter your email';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return 'Enter a valid email';
  return '';
}

function validatePassword(pw) {
  if (!pw) return 'Enter a password';
  if (pw.length < 8) return 'At least 8 characters';
  if (!/[A-Z]/.test(pw)) return 'Include an uppercase letter';
  if (!/[a-z]/.test(pw)) return 'Include a lowercase letter';
  if (!/\d/.test(pw)) return 'Include a number';
  if (!/[^A-Za-z0-9]/.test(pw)) return 'Include a special character';
  return '';
}

// ── Component ────────────────────────────────────────────────────────

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const auth = useAuth();

  // Steps: 'identifier' → 'reset'
  const [step, setStep] = useState('identifier');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLeft, setResendLeft] = useState(0);

  // Store the username returned by forgotPassword
  const [resetUsername, setResetUsername] = useState('');

  const { userType, loginMethod, setUserType, setLoginMethod } = auth;

  // ── validation ───────────────────────────────────────────────────
  const phoneError = useMemo(
    () => (loginMethod === 'PHONE' && step === 'identifier' ? validationMessage(phone) : ''),
    [phone, loginMethod, step],
  );
  const emailError = useMemo(
    () => (loginMethod === 'EMAIL' && step === 'identifier' ? validateEmail(email) : ''),
    [email, loginMethod, step],
  );
  const passwordError = useMemo(() => (step === 'reset' ? validatePassword(newPassword) : ''), [newPassword, step]);
  const confirmError = useMemo(
    () => (step === 'reset' && confirmPassword && confirmPassword !== newPassword ? 'Passwords do not match' : ''),
    [newPassword, confirmPassword, step],
  );

  const identifierValid =
    loginMethod === 'PHONE' ? !phoneError && phone.length > 0 : !emailError && email.length > 0;

  // ── resend countdown ─────────────────────────────────────────────
  useEffect(() => {
    if (step === 'reset' && resendLeft > 0) {
      const id = setInterval(() => setResendLeft((s) => Math.max(0, s - 1)), 1000);
      return () => clearInterval(id);
    }
  }, [step, resendLeft]);

  // ── Step 1: request reset OTP ────────────────────────────────────
  const handleRequestReset = async () => {
    setError('');
    setSuccess('');
    if (!identifierValid) return;
    setLoading(true);
    try {
      const payload = { userType, loginMethod };
      if (loginMethod === 'PHONE') {
        const { e164 } = normalizeIndianMobile(phone);
        payload.phone = e164;
      } else {
        payload.email = email.trim();
      }

      await forgotPassword(payload);

      // The username to use in the reset call
      setResetUsername(
        loginMethod === 'PHONE' ? normalizeIndianMobile(phone).e164 : email.trim(),
      );

      setStep('reset');
      setResendLeft(TIMING.RESEND_OTP_COOLDOWN);
    } catch (e) {
      setError(getAuthErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: submit new password + OTP ────────────────────────────
  const handleResetPassword = async () => {
    setError('');
    setSuccess('');
    if (otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }
    if (passwordError) {
      setError(passwordError);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await resetPassword({
        userType,
        username: resetUsername,
        otp,
        newPassword,
      });

      setSuccess('Password reset successful! Redirecting to login…');
      setTimeout(() => navigate(ROUTES.LOGIN, { replace: true }), 2000);
    } catch (e) {
      setError(getAuthErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  // ── resend ───────────────────────────────────────────────────────
  const handleResend = async () => {
    setError('');
    setOtp('');
    if (resendLeft > 0) return;
    try {
      const payload = { userType, loginMethod };
      if (loginMethod === 'PHONE') {
        const { e164 } = normalizeIndianMobile(phone);
        payload.phone = e164;
      } else {
        payload.email = email.trim();
      }
      await forgotPassword(payload);
      setResendLeft(TIMING.RESEND_OTP_COOLDOWN);
    } catch (e) {
      setError(getAuthErrorMessage(e));
    }
  };

  // ── reset when selectors change ──────────────────────────────────
  const handleUserTypeChange = (val) => {
    setUserType(val);
    resetForm();
  };
  const handleLoginMethodChange = (val) => {
    setLoginMethod(val);
    resetForm();
  };
  const resetForm = () => {
    setStep('identifier');
    setPhone('');
    setEmail('');
    setOtp('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess('');
    setResendLeft(0);
  };

  // ── render ───────────────────────────────────────────────────────
  const identifierLabel = loginMethod === 'PHONE' ? phone : email;

  return (
    <AuthCard title="Reset Password" subtitle="We'll send you a code to reset your password" error={error}>
      {success && (
        <div
          style={{
            padding: 12,
            marginBottom: 16,
            background: theme.auth.successBg || '#e6f9e6',
            border: `1px solid ${theme.auth.successBorder || '#4caf50'}`,
            color: theme.auth.successText || '#2e7d32',
            borderRadius: 8,
            fontSize: 14,
          }}
        >
          {success}
        </div>
      )}

      <UserTypeSelector value={userType} onChange={handleUserTypeChange} disabled={loading} />
      <LoginMethodSelector value={loginMethod} onChange={handleLoginMethodChange} disabled={loading || step === 'reset'} />

      {/* ── Step 1: identifier ──────────────────────────────────── */}
      {step === 'identifier' && (
        <>
          {loginMethod === 'PHONE' ? (
            <PhoneInput value={phone} onChange={setPhone} error={phoneError} disabled={loading} />
          ) : (
            <EmailInput value={email} onChange={setEmail} error={emailError} disabled={loading} />
          )}
          <div style={{ height: 12 }} />
          <PrimaryButton onClick={handleRequestReset} disabled={!identifierValid || loading}>
            {loading ? 'Please wait…' : 'Send Reset Code'}
          </PrimaryButton>
        </>
      )}

      {/* ── Step 2: OTP + new password ──────────────────────────── */}
      {step === 'reset' && (
        <>
          <div style={{ marginBottom: 8, fontSize: 14, color: theme.auth.labelColor }}>
            We sent a code to <strong>{identifierLabel}</strong>
          </div>
          <OtpInput value={otp} onChange={setOtp} disabled={loading} />
          <div style={{ height: 12 }} />
          <PasswordInput
            value={newPassword}
            onChange={setNewPassword}
            error={passwordError}
            disabled={loading}
            label="New Password"
            placeholder="Enter new password"
          />
          <div style={{ height: 12 }} />
          <PasswordInput
            value={confirmPassword}
            onChange={setConfirmPassword}
            error={confirmError}
            disabled={loading}
            label="Confirm Password"
            placeholder="Re-enter new password"
          />
          <div style={{ height: 12 }} />
          <PrimaryButton
            onClick={handleResetPassword}
            disabled={
              loading ||
              otp.length !== 6 ||
              !!passwordError ||
              !!confirmError ||
              !newPassword ||
              !confirmPassword
            }
          >
            {loading ? 'Resetting…' : 'Reset Password'}
          </PrimaryButton>
          <ResendOtp onClick={handleResend} disabled={resendLeft > 0 || loading} seconds={resendLeft} />
        </>
      )}

      {/* footer link */}
      <div style={{ marginTop: 20, textAlign: 'center', fontSize: 14, color: theme.auth.mutedText }}>
        Remember your password?{' '}
        <Link to={ROUTES.LOGIN} style={{ color: theme.auth.buttonPrimary, textDecoration: 'none', fontWeight: 600 }}>
          Log In
        </Link>
      </div>
    </AuthCard>
  );
}
